/**
 * Fixed FX rates (NOT live).
 *
 * Rate semantics: `1 unit of CCY == rate TWD`, ported from
 * `design/project/shared.jsx CURRENCIES`. Conversion pivots through TWD.
 *
 * Storage convention: amounts are 2-decimal minor units (`100 * major`)
 * regardless of currency. So `1000 JPY` (which is whole-yen) stores as
 * `100_000` minor; `12.34 USD` stores as `1234` minor. FX preserves the
 * 2-decimal scale: convertMinor(100_000, 'JPY', 'TWD') ≈ 21_400 minor
 * (= 214 TWD).
 *
 * "Unknown" currency falls through with rate=1 — i.e. treated as already
 * being primary. This is a defensive default; the UI should never feed
 * an unknown code (validation lives in repos).
 */

export interface FxRate {
  /** ISO 4217 code, uppercase. */
  code: string;
  /** "1 CCY = rate TWD". TWD itself is 1. */
  rate: number;
}

export const FX_RATES: Readonly<Record<string, FxRate>> = {
  TWD: { code: 'TWD', rate: 1 },
  USD: { code: 'USD', rate: 32.4 },
  JPY: { code: 'JPY', rate: 0.214 },
  EUR: { code: 'EUR', rate: 35.2 },
  KRW: { code: 'KRW', rate: 0.0235 },
  CNY: { code: 'CNY', rate: 4.48 },
  HKD: { code: 'HKD', rate: 4.14 },
  THB: { code: 'THB', rate: 0.93 },
  SGD: { code: 'SGD', rate: 24.1 },
};

function rateOf(code: string): number {
  const r = FX_RATES[code.toUpperCase()];
  return r ? r.rate : 1;
}

/**
 * Convert a minor-unit amount from one currency to another via TWD pivot.
 * Both inputs are in 2-decimal minor units; output is in 2-decimal minor
 * units of the target currency. Result is rounded to nearest minor unit.
 *
 * No-op fast path when from === to (avoids floating-point drift).
 */
export function convertMinor(amountMinor: number, from: string, to: string): number {
  if (!Number.isFinite(amountMinor)) return 0;
  const f = from.toUpperCase();
  const t = to.toUpperCase();
  if (f === t) return amountMinor;
  const rf = rateOf(f);
  const rt = rateOf(t);
  if (rt === 0) return 0;
  return Math.round((amountMinor * rf) / rt);
}

export interface ConvertibleTransaction {
  amountMinor: number;
  currency: string;
}

/**
 * Sum a list of (amount, currency) pairs into one total expressed in `to`.
 * Each tx is FX-converted via `convertMinor` then summed. Used by
 * BalanceCard / Analytics so the headline totals are single-currency.
 */
export function sumMinorAs(
  txs: readonly ConvertibleTransaction[],
  to: string,
): number {
  let acc = 0;
  for (const t of txs) {
    acc += convertMinor(t.amountMinor, t.currency || 'TWD', to);
  }
  return acc;
}
